package org.zest.insan_360_website.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.CoroutineScope
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.zest.insan_360_website.components.layouts.*
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.AssessmentPageState
import org.zest.insan_360_website.data.page_states.CreditState

import com.varabyte.kobweb.silk.style.CssStyle

val modalButtonContainerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.ColumnReverse)
            .justifyContent(JustifyContent.SpaceBetween)
            .gap(1.5.cssRem)
            .margin(top = 0.75.cssRem)
    }
    Breakpoint.MD {
        Modifier.flexDirection(FlexDirection.Row)
    }
}

val modalButtonStyle = CssStyle {
    base {
        Modifier
            .fillMaxHeight()
            .fillMaxWidth()
    }
    Breakpoint.MD {
        Modifier.flex(1)
    }
}

@Composable
fun SessionExpiredModal(ctx: PageContext, scope: CoroutineScope) {
    val wordLibrary = LanguageSettings.AssessmentPage.SessionExpiredModal
    val modal = LocalModal.current
    val toast = LocalToast.current
    var isExiting by remember { mutableStateOf(false) }
    var isExtending by remember { mutableStateOf(false) }
    val isProcessing = isExiting || isExtending

    DisposableEffect(Unit) {
        modal.show(closeableOutside = false) {
            Column(
                Modifier
                    .maxHeight(90.vh)
                    .fontSize(1.5.cssRem)
                    .gap(0.75.cssRem)
                    .userSelect(UserSelect.None)
            ) {
                if (CreditState.activeCreditSession.extensionCount < 2) {
                    SpanText(
                        wordLibrary.timer_run_out.localized,
                        Modifier
                            .fillMaxWidth()
                            .fontWeight(FontWeight.SemiBold)
                            .fontSize(1.875.cssRem)
                            .color(userPagePrimaryTextColor)
                            .textAlign(TextAlign.Center)
                    )
                    SpanText(
                        wordLibrary.continue_extend_or_exit.localized,
                        Modifier
                            .fontWeight(FontWeight.Medium)
                            .fontSize(1.5.cssRem)
                            .color(userPagePrimaryTextColor)
                            .textAlign(TextAlign.Center)
                    )
                    SpanText(
                        wordLibrary.exit_warning.localized,
                        Modifier
                            .fontWeight(FontWeight.Normal)
                            .fontSize(1.125.cssRem)
                            .color(userPageSecondaryTextColor)
                            .textAlign(TextAlign.Center)
                    )
                    Div(
                        modalButtonContainerStyle.toModifier().toAttrs()
                    ) {
                        Box(
                            contentRedButtonStyle.toModifier()
                                .then(modalButtonStyle.toModifier())
                                .thenIf(isProcessing) { Modifier.opacity(0.5).cursor(Cursor.NotAllowed) }
                                .onClick {
                                    if (isProcessing) return@onClick
                                    isExiting = true
                                    AssessmentPageState.terminateUnfulfilledAssessment(scope, toast, ctx) {
                                        isExiting = false
                                    }
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                if (isExiting) wordLibrary.exiting.localized else wordLibrary.exit_session.localized,
                                contentButtonTextStyle.toModifier().textAlign(TextAlign.Center)
                            )
                        }
                        Box(
                            contentButtonStyle.toModifier()
                                .then(modalButtonStyle.toModifier())
                                .thenIf(isProcessing) { Modifier.opacity(0.5).cursor(Cursor.NotAllowed) }
                                .onClick {
                                    if (isProcessing) return@onClick
                                    isExtending = true
                                    CreditState.handleExtendCreditSession(
                                        scope = scope,
                                        toast = toast,
                                        onComplete = { isExtending = false },
                                        onSuccess = {
                                            modal.hide()
                                            CreditState.needUpdate = true
                                        }
                                    )
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                if (isExtending) wordLibrary.extending.localized else wordLibrary.extend.localized,
                                contentButtonTextStyle.toModifier().textAlign(TextAlign.Center)
                            )
                        }
                    }
                } else {
                    SpanText(
                        wordLibrary.timer_completely_run_out.localized,
                        Modifier
                            .fillMaxWidth()
                            .fontWeight(FontWeight.SemiBold)
                            .fontSize(1.875.cssRem)
                            .color(userPagePrimaryTextColor)
                            .textAlign(TextAlign.Center)
                    )
                    SpanText(
                        wordLibrary.unable_to_finish.localized,
                        Modifier
                            .fontWeight(FontWeight.Medium)
                            .fontSize(1.5.cssRem)
                            .color(userPagePrimaryTextColor)
                            .textAlign(TextAlign.Center)
                    )
                    SpanText(
                        wordLibrary.enquiries_contact_us.localized,
                        Modifier
                            .fillMaxWidth()
                            .fontWeight(FontWeight.Normal)
                            .fontSize(1.125.cssRem)
                            .color(userPageSecondaryTextColor)
                            .textAlign(TextAlign.Center)
                    )
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .gap(1.5.cssRem)
                            .margin(top = 0.75.cssRem),
                        horizontalArrangement = Arrangement.Center,
                    ) {
                        Box(
                            contentRedButtonStyle.toModifier()
                                .then(modalButtonStyle.toModifier())
                                .thenIf(isProcessing) { Modifier.opacity(0.5).cursor(Cursor.NotAllowed) }
                                .onClick {
                                    if (isProcessing) return@onClick
                                    isExiting = true
                                    AssessmentPageState.terminateUnfulfilledAssessment(scope, toast, ctx) {
                                        isExiting = false
                                    }
                                }
                        ) {
                            SpanText(
                                if (isExiting) wordLibrary.exiting.localized else wordLibrary.exit_session.localized,
                                contentButtonTextStyle.toModifier().textAlign(TextAlign.Center)
                            )
                        }
                    }
                }
            }
        }
        
        onDispose {
            modal.hide(fromBackButton = true)
        }
    }
}